/**
 * ชื่อแดงพเนจร (data/redRoamers.ts): a simulated red name who now and then roams a big zone's wilds, offline.
 *
 * - Every `rollEvery` s while the player is in the wilds (a layout region, outside the safe outline), past the first
 *   hour's quests and with nobody roaming, a roll of `chance` brings one, `spawnMin`–`spawnMax` m away.
 * - He walks toward the player with PK off. Within `warnAt` m the World warns (`redRoamer` `warn`, a notice); only
 *   `warnSeconds` later does PK go on, and the rivals' own brain (world.ts `updateRival`) strikes within 9 m.
 *   Past `giveUp` m, or with the player in a safe zone, PK goes off again: running away works (he is slower).
 * - Killed, or after `life` s once the player is far, he leaves the world; the next roll waits `cooldown` s.
 * - His kill is a red name's kill: systems/social.ts offers "เพิ่มเป็นคู่แค้น".
 */
import { ARENA_ROSTER } from '../data/arena';
import { RED_ROAMERS as R } from '../data/redRoamers';
import { getArt, getSkill } from '../data/skills';
import { freeAt } from '../path';
import type { Entity, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

/** Events (part of `WorldEvent`). */
export type RedRoamerEvent = { type: 'redRoamer'; what: 'appear' | 'warn' | 'giveUp' | 'leave'; id: number; name: string };

export class RedRoamersSystem implements WorldSystem {
  readonly name = 'redRoamers';
  private id = -1;
  private rollT: number = R.rollEvery;
  private coolT = 0;
  private lifeT = 0;
  /** Seconds left of the warning (−1: not warned yet). */
  private warnT = -1;

  constructor(private readonly h: SystemHost) {}

  /** The roamer now, if any. */
  roamer(): Entity | undefined {
    return this.id < 0 ? undefined : this.h.world.get(this.id);
  }

  private inWilds(x: number, y: number): boolean {
    const w = this.h.world;
    if (w.inSafeZone({ x, y })) return false;
    return Object.values(w.map.regions).some(([i0, i1, j0, j1]) => x >= i0 && x <= i1 && y >= j0 && y <= j1);
  }

  private eligible(): boolean {
    const w = this.h.world;
    return (
      R.zones.includes(w.map.zone) &&
      !w.interiors.inside &&
      !w.duel &&
      w.progress.quest.index >= R.minQuest &&
      !w.player.dead &&
      this.inWilds(w.player.x, w.player.y)
    );
  }

  observe(e: WorldEvent): void {
    if (e.type === 'death' && e.id === this.id) this.lifeT = R.life + 1e6; // leaves at the next step
  }

  step(dt: number): void {
    const r = this.roamer();
    if (this.id >= 0 && !r) this.id = -1;
    if (r) return this.watch(r, dt);
    if ((this.coolT -= dt) > 0 || (this.rollT -= dt) > 0) return;
    this.rollT = R.rollEvery;
    if (!this.eligible()) return;
    if (this.h.random() >= R.chance) return;
    this.spawn();
  }

  private spawn(): void {
    const w = this.h.world;
    const p = w.player;
    for (let k = 0; k < 8; k++) {
      const a = this.h.random() * Math.PI * 2;
      const d = R.spawnMin + this.h.random() * (R.spawnMax - R.spawnMin);
      const x = p.x + Math.cos(a) * d;
      const y = p.y + Math.sin(a) * d;
      if (x < 1 || y < 1 || x > w.map.w - 1 || y > w.map.h - 1 || !freeAt(w.map, x, y) || !this.inWilds(x, y)) continue;
      // The weaker half of the arena's roster: their names, looks and moves.
      const half = Math.floor(ARENA_ROSTER.length / 2);
      const b = ARENA_ROSTER[half + Math.floor(this.h.random() * (ARENA_ROSTER.length - half))]!;
      const e = this.h.add({
        kind: 'rival',
        x,
        y,
        name: b.name,
        title: 'ชื่อแดงพเนจร',
        sprite: b.sprite,
        speed: p.speed * R.speed,
        level: p.level,
        rival: {
          homeX: x,
          homeY: y,
          aggroT: 0,
          skillT: 2,
          skills: getArt(b.art)
            .skills.filter((id) => ['attack', 'break', 'power'].includes(getSkill(id).kind))
            .slice(0, 4),
        },
      });
      Object.assign(e, {
        maxHp: Math.round(p.maxHp * R.hp),
        hp: Math.round(p.maxHp * R.hp),
        maxQi: p.maxQi,
        qi: p.maxQi,
        atk: p.atk * R.atk,
        def: p.def,
        magic: p.magic * R.atk,
        magicDef: p.magicDef,
        hit: p.hit,
        magicHit: p.magicHit,
        dodge: p.dodge,
        crit: p.crit,
        pk: false,
        pkPoints: R.pkPoints,
        karma: 'evil',
      });
      this.id = e.id;
      this.lifeT = 0;
      this.warnT = -1;
      this.h.emit({ type: 'redRoamer', what: 'appear', id: e.id, name: e.name });
      return;
    }
  }

  private watch(r: Entity, dt: number): void {
    const w = this.h.world;
    const p = w.player;
    this.lifeT += dt;
    const d = Math.hypot(r.x - p.x, r.y - p.y);
    if (r.dead || (this.lifeT > R.life && d > R.leaveAt)) return this.leave(r);
    const safe = w.inSafeZone(p) || p.dead;
    if (r.pk && (d > R.giveUp || safe)) {
      r.pk = false;
      this.warnT = -1;
      this.h.emit({ type: 'redRoamer', what: 'giveUp', id: r.id, name: r.name });
      if (!p.dead) this.h.notice(`${r.name} เลิกไล่ตามท่านแล้ว`);
    }
    if (r.pk || safe) return;
    // Walks the player's way (his home follows the player) until close enough to warn.
    if (this.warnT < 0) {
      r.rival!.homeX = p.x;
      r.rival!.homeY = p.y;
      if (d <= R.warnAt) {
        this.warnT = R.warnSeconds;
        this.h.notice(`ระวัง! ${r.name} (ชื่อแดง) ชักกระบี่เดินเข้ามา · หนีหรือเตรียมสู้`);
        this.h.emit({ type: 'redRoamer', what: 'warn', id: r.id, name: r.name });
      }
      return;
    }
    if ((this.warnT -= dt) <= 0) r.pk = true;
  }

  private leave(r: Entity): void {
    const w = this.h.world;
    w.entities.delete(r.id);
    if (w.player.targetId === r.id) w.player.targetId = null;
    this.id = -1;
    this.coolT = R.cooldown;
    this.rollT = R.rollEvery;
    this.h.emit({ type: 'redRoamer', what: 'leave', id: r.id, name: r.name });
  }
}
